import { StudentEmployerPlacementStatus } from '@duke-hq/svc-api-db/client';
import type { TransactionClient } from '@duke-hq/svc-api-db/extensions';
import {
  activePlacementConflictError,
  rethrowActivePlacementConflictForIndex,
} from './placementConflicts';
import type { UniqueIndex } from '@duke-hq/svc-api/lib/prismaConflicts';

export const TERMINAL_EMPLOYER_PLACEMENT_STATUSES = new Set<StudentEmployerPlacementStatus>([
  StudentEmployerPlacementStatus.COMPLETED,
  StudentEmployerPlacementStatus.FAILED,
  StudentEmployerPlacementStatus.CANCELLED,
]);

export function isActiveEmployerPlacement(status: StudentEmployerPlacementStatus): boolean {
  return !TERMINAL_EMPLOYER_PLACEMENT_STATUSES.has(status);
}

type PlacementClientForActiveCheck = {
  studentEmployerPlacement: Pick<TransactionClient['studentEmployerPlacement'], 'findFirst'>;
};

export async function assertNoActivePlacementForJourney(
  journeyId: string,
  client: PlacementClientForActiveCheck,
  { excludePlacementId }: { excludePlacementId?: string } = {},
): Promise<void> {
  const existing = await client.studentEmployerPlacement.findFirst({
    where: {
      journeyId,
      deletedAt: null,
      status: { notIn: [...TERMINAL_EMPLOYER_PLACEMENT_STATUSES] },
      ...(excludePlacementId != null && { id: { not: excludePlacementId } }),
    },
    select: { id: true },
  });

  if (existing != null) {
    throw activePlacementConflictError();
  }
}

export const ACTIVE_EMPLOYER_PLACEMENT_INDEX: UniqueIndex = {
  name: 'student_employer_placements_one_active_per_journey_key',
  columns: ['journey_id'],
};

export function rethrowActiveEmployerPlacementConflict(error: unknown): never {
  rethrowActivePlacementConflictForIndex(error, ACTIVE_EMPLOYER_PLACEMENT_INDEX);
}

type PlacementTimestamps = { startedAt: Date | null; endedAt: Date | null };

export function computePlacementTimestamps(
  toStatus: StudentEmployerPlacementStatus,
  now: Date,
  current: PlacementTimestamps,
): PlacementTimestamps {
  if (toStatus === StudentEmployerPlacementStatus.IN_PROGRESS) {
    return { startedAt: now, endedAt: null };
  }
  if (TERMINAL_EMPLOYER_PLACEMENT_STATUSES.has(toStatus)) {
    return { startedAt: current.startedAt, endedAt: now };
  }
  return { startedAt: current.startedAt, endedAt: current.endedAt };
}

export function placementTransitionRequiresCascade(
  fromStatus: StudentEmployerPlacementStatus,
  toStatus: StudentEmployerPlacementStatus,
): boolean {
  return (
    toStatus === StudentEmployerPlacementStatus.COMPLETED ||
    fromStatus === StudentEmployerPlacementStatus.COMPLETED
  );
}
